import classNames from "classnames";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Card, { CardBody } from "../../../Components/base/Card";
import Icon from "../../../Components/base/Icon";
import ConfirmationModal from "../../../Components/base/Modal/ConfirmationModal";
import DataTable from "../../../Components/base/Table/DataTable";
import PaginationButtons, {
  PER_COUNT,
} from "../../../Components/base/Table/Pagination/PaginationButtons";
import Button from "../../../Components/base/forms/Button";
import Input from "../../../Components/base/forms/Input";
import Page from "../../../Components/layout/Page";
import PageWrapper from "../../../Components/layout/PageWrapper";
import SubHeader, {
  SubHeaderLeft,
  SubHeaderRight,
} from "../../../Core/FrameLayouts/SubHeader/SubHeader";
import useDarkMode from "../../../Hooks/useDarkMode";
import useSortableData from "../../../Hooks/useSortableData";
import {
  deleteWebMenu,
  getWebMenuData,
} from "../../../Services/WebMenuServices";
import AddWebMenuDialog from "./AddWebMenuDialog";
import { configurationMenu } from "../../../menu";

const WebMenu = () => {
  const { darkModeStatus } = useDarkMode();

  const dispatch = useDispatch();
  const { webMenuData } = useSelector((state) => state.webMenu);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_COUNT["10"]);
  const [filterMenu, setFilterMenu] = useState(false);

  const { items, requestSort, getClassNamesFor } = useSortableData(webMenuData);
  const [isOpenAddWebMenuDialog, setIsOpenAddWebMenuDialog] = useState(false);
  const [webMenuItems, setWebMenuItems] = useState({});

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState();

  useEffect(() => {
    dispatch(getWebMenuData());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      searchInput: "",
      webMenuData: [],
      status: "",
    },
    onSubmit: (values) => {
      setFilterMenu(false);
    },
  });

  const handleOnClickDelete = async (id) => {
    setIsOpenDeleteModal(true);
    setDeleteId(id);
  };

  const handleDeleteWebMenu = async () => {
    setIsOpenDeleteModal(false);
    try {
      const requestBody = {};
      const response = await dispatch(deleteWebMenu(requestBody, deleteId));
      if (response.status_code === 200) {
        toast.success(response.message);
        dispatch(getWebMenuData());
      } else {
        toast.error(
          response.message || "Web Menu delete failed. Please try again.",
        );
        console.error("Error Web Menu delete", response);
      }
    } catch (error) {
      console.error("API call error", error);
    }
  };

  const columns = [
    { key: "id", label: "#", sortable: true },
    { key: "name", label: "Title", sortable: true },
    {
      key: "parent_id",
      label: "Parents",
      sortable: false,
      isCenter: true,
      render: (item) => (item.parent ? item.parent.name : "Main Menu"),
    },
    { key: "order_no", label: "Orders", sortable: false, isCenter: true },
    {
      key: "actions",
      label: "Actions",
      isCenter: true,
      render: (item) => (
        <>
          <Button
            isOutline={!darkModeStatus}
            color=""
            isLight={darkModeStatus}
            className={classNames("text-nowrap table-action-btn text-info")}
            icon="Eye"
            tag="a"
            to={`../${configurationMenu.configuration.subMenu.viewWebMenu.path}/${item.id}`}
          />
          <Button
            isOutline={!darkModeStatus}
            color=""
            isLight={darkModeStatus}
            className={classNames(
              "text-nowrap table-action-btn ms-2 text-success",
            )}
            icon="Edit"
            onClick={() => {
              setWebMenuItems(item);
              setIsOpenAddWebMenuDialog(true);
            }}
          />
          <Button
            isOutline={!darkModeStatus}
            color=""
            isLight={darkModeStatus}
            className={classNames(
              "text-nowrap table-action-btn ms-2 text-danger",
            )}
            icon="Trash2"
            onClick={() => {
              handleOnClickDelete(item.id);
            }}
          />
        </>
      ),
    },
  ];

  const filteredItems = items.filter((item) =>
    Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(formik.values.searchInput.toLowerCase()),
  );

  const renderSubHeader = () => {
    return (
      <SubHeader>
        <SubHeaderLeft>
          <label
            className="border-0 bg-transparent cursor-pointer me-0"
            htmlFor="searchInput"
          >
            <Icon
              iconName="Search"
              style={{ color: "primary", fontSize: "2x" }}
            />
          </label>
          <Input
            id="searchInput"
            type="search"
            className="border-0 shadow-none bg-transparent"
            placeholder="Search web menu..."
            onChange={formik.handleChange}
            value={formik.values.searchInput}
          />
        </SubHeaderLeft>
        <SubHeaderRight>
          <Button
            icon="UserPlus"
            color="primary"
            isLight
            onClick={() => {
              setIsOpenAddWebMenuDialog(true);
              setWebMenuItems({});
            }}
          >
            Add Menu
          </Button>
        </SubHeaderRight>
      </SubHeader>
    );
  };

  return (
    <PageWrapper title={"Menu Manager"} headerTitle={"Menu Manager"}>
      {renderSubHeader()}
      <Page>
        <div className="row h-100">
          <div className="col-12">
            <Card stretch>
              <CardBody isScrollable className="table-responsive">
                <DataTable
                  columns={columns}
                  data={filteredItems}
                  perPage={perPage}
                  currentPage={currentPage}
                  requestSort={requestSort}
                  getClassNamesFor={getClassNamesFor}
                  darkModeStatus={darkModeStatus}
                />
              </CardBody>
              <PaginationButtons
                data={filteredItems}
                label={"Menus"}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                perPage={perPage}
                setPerPage={setPerPage}
              />
            </Card>
          </div>
        </div>
      </Page>
      <AddWebMenuDialog
        setIsOpen={setIsOpenAddWebMenuDialog}
        isOpen={isOpenAddWebMenuDialog}
        itemData={webMenuItems}
        webMenuData={webMenuData}
        fromSource="webMenu"
      />
      <ConfirmationModal
        isOpen={isOpenDeleteModal}
        setIsOpen={setIsOpenDeleteModal}
        title="Confirm Delete"
        message="Are you sure you want to delete this record?"
        btnTitle={"Delete"}
        handleOnConfirm={handleDeleteWebMenu}
      />
    </PageWrapper>
  );
};

export default WebMenu;